/**
 * Podcast Episodes Fetcher
 *
 * Server-side fetcher for podcast episodes, used in CMS preview mode
 * when episodes aren't pre-populated by the server-side hook.
 *
 * Uses "use server" to run as an RPC call, avoiding CORS/GTM issues.
 * Works with createResource — the async function signature is unchanged.
 */

import type { CMSPodcastEpisode, CMSPodcastEpisodeResponse } from "~/types/cms";
import { env } from "~/config/env";
import { isPreviewTokenValid } from "~/utils/previewToken.server";
import { getApiSortParam, type EpisodeSortOrder } from "~/utils/podcastSort";
import { fetchFromCMS } from "../queryUtils";

/**
 * Fetch episodes for a podcast (server-side RPC)
 *
 * Used in preview mode when episodes aren't populated by the server-side hook.
 * Plain async function suitable for use with createResource.
 *
 * NOTE: Payload REST API doesn't support querying by nested relationship fields
 * (e.g., where[podcast.slug][equals]=...). We must use the podcast ID directly.
 *
 * @param podcastId - The podcast ID (not slug)
 * @param options - Fetch options
 * @returns Array of episodes
 */
export async function fetchEpisodesByPodcastId(
  podcastId: string,
  options: {
    sortOrder?: EpisodeSortOrder;
    limit?: number;
    previewToken?: string;
  } = {}
): Promise<CMSPodcastEpisode[]> {
  "use server";

  const { sortOrder = "newest", limit = 100, previewToken } = options;
  const draft = isPreviewTokenValid(previewToken, env.tenantSlug);

  const sort = getApiSortParam(sortOrder);

  // Build URL with query params
  const params = new URLSearchParams({
    depth: "2",
    limit: String(limit),
    sort,
    "where[podcast][equals]": podcastId,
    // Dot-syntax tenant filter is honored server-side; bracket-syntax is silently ignored.
    "where[tenant.slug][equals]": env.tenantSlug,
  });

  if (draft) {
    params.set("draft", "true");
  }

  const url = `${env.cmsApiUrl}/api/podcast-episodes?${params.toString()}`;

  const data = await fetchFromCMS<CMSPodcastEpisodeResponse>(url, {
    logPrefix: "[podcastEpisodes]",
    previewToken: draft ? previewToken : undefined,
  });

  return data?.docs ?? [];
}
